import { BLUE_600, GRAY_100, GRAY_200, GRAY_400, GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { Box, Typography } from '@mui/material'
import React, { useCallback, useState } from 'react'

import { Stack } from '@mui/material'
import carpetDarkblue from 'assets/img/floor_replacement/carpet-darkblue.webp'
import carpetGrey from 'assets/img/floor_replacement/carpet-grey.webp'
import concreteLightgrey from 'assets/img/floor_replacement/concrete-lightgrey.webp'
// Import floor template images
import hardwoodAria from 'assets/img/floor_replacement/hardwood-aria.webp'
import hardwoodLightbrown from 'assets/img/floor_replacement/hardwood-lightbrown.webp'
import tilesGrey from 'assets/img/floor_replacement/tiles-grey.webp'
import tilesWhite from 'assets/img/floor_replacement/tiles-white.webp'
import { useTranslation } from 'react-i18next'

export interface FloorTemplate {
  id: string
  apiName: string
  nameKey: string
  thumbnailUrl: string
}

export const FLOOR_TEMPLATES: FloorTemplate[] = [
  { id: 'hardwood_aria', apiName: 'hardwood:aria', nameKey: 'virtual_staging.floor_templates.hardwood_aria', thumbnailUrl: hardwoodAria },
  { id: 'hardwood_lightbrown', apiName: 'hardwood:lightbrown', nameKey: 'virtual_staging.floor_templates.hardwood_lightbrown', thumbnailUrl: hardwoodLightbrown },
  { id: 'tiles_grey', apiName: 'tiles:grey', nameKey: 'virtual_staging.floor_templates.tiles_grey', thumbnailUrl: tilesGrey },
  { id: 'tiles_white', apiName: 'tiles:white', nameKey: 'virtual_staging.floor_templates.tiles_white', thumbnailUrl: tilesWhite },
  { id: 'carpet_grey', apiName: 'carpet:grey', nameKey: 'virtual_staging.floor_templates.carpet_grey', thumbnailUrl: carpetGrey },
  { id: 'carpet_darkblue', apiName: 'carpet:darkblue', nameKey: 'virtual_staging.floor_templates.carpet_darkblue', thumbnailUrl: carpetDarkblue },
  { id: 'concrete_lightgrey', apiName: 'concrete:lightgrey', nameKey: 'virtual_staging.floor_templates.concrete_lightgrey', thumbnailUrl: concreteLightgrey },
]

interface FloorTemplatePickerProps {
  /** The currently selected floor template */
  selectedTemplate: string | null
  /** Callback function called when template changes */
  onTemplateChange: (template: string) => void
  /** Whether the component should be disabled (e.g., during generation) */
  isDisabled?: boolean
}

/**
 * FloorTemplatePicker component for Virtual Staging.
 * Displays a grid of floor template options similar to StyleSelector.
 * 
 * @example
 * <FloorTemplatePicker
 *   selectedTemplate={virtualStagingState.floorTemplate}
 *   onTemplateChange={handleTemplateChange}
 *   isDisabled={virtualStagingState.isGeneratingStaging}
 * />
 */
export const FloorTemplatePicker: React.FC<FloorTemplatePickerProps> = ({
  selectedTemplate,
  onTemplateChange,
  isDisabled = false,
}) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const [loadingImages, setLoadingImages] = useState<Set<string>>(new Set())
  const [failedImages, setFailedImages] = useState<Set<string>>(new Set())

  // Default to first template if none selected
  const effectiveSelectedTemplate = selectedTemplate || FLOOR_TEMPLATES[0].id

  const handleTemplateClick = (templateId: string) => {
    if (!isDisabled) {
      onTemplateChange(templateId)
    }
  }

  // Handle image loading states
  const handleImageLoad = useCallback((templateId: string) => {
    setLoadingImages(prev => {
      const newSet = new Set(prev)
      newSet.delete(templateId)
      return newSet
    })
    setFailedImages(prev => {
      const newSet = new Set(prev)
      newSet.delete(templateId)
      return newSet
    })
  }, [])

  const handleImageError = useCallback((templateId: string) => {
    setLoadingImages(prev => {
      const newSet = new Set(prev)
      newSet.delete(templateId)
      return newSet
    })
    setFailedImages(prev => {
      const newSet = new Set(prev)
      newSet.add(templateId)
      return newSet
    })
  }, [])

  const handleImageLoadStart = useCallback((templateId: string) => {
    setLoadingImages(prev => {
      const newSet = new Set(prev)
      newSet.add(templateId)
      return newSet
    })
  }, [])

  return (
    <Stack gap={0.8}>
      <Typography
        variant="text-md"
        sx={{
          fontWeight: 550,
          color: GRAY_900,
          lineHeight: 1.5,
          letterSpacing: '-0.64px',
        }}
      >
        {t('virtual_staging.floor_templates.title')}
      </Typography>
      <Stack
        sx={{
          width: '100%',
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '1.2rem',
          cursor: isDisabled ? 'not-allowed' : 'pointer',
          pointerEvents: isDisabled ? 'none' : 'auto',
        }}
      >
        {FLOOR_TEMPLATES.map((template, index) => {
          const isSelected = effectiveSelectedTemplate === template.id
          return (
            <Box
              key={template.id}
              onClick={() => handleTemplateClick(template.id)}
              tabIndex={isDisabled ? -1 : 0}
              role="button"
              aria-label={`${t('virtual_staging.floor_templates.select_template', 'Select floor template')}: ${t(template.nameKey)}`}
              sx={{
                cursor: isDisabled ? 'not-allowed' : 'pointer',
                opacity: isDisabled ? 0.6 : 1,
                transition: 'transform 0.2s ease-in-out',
                animation: `fadeInUp 0.5s ease-out ${index * 0.1}s both`,
                '@keyframes fadeInUp': {
                  '0%': {
                    opacity: 0,
                    transform: 'translateY(1rem)',
                  },
                  '100%': {
                    opacity: 1,
                    transform: 'translateY(0)',
                  },
                },
                '&:hover': !isDisabled ? {
                  transform: 'translateY(-0.2rem)',
                } : {},
              }}
            >
              {/* Template Thumbnail */}
              <Box
                sx={{
                  width: '100%',
                  aspectRatio: '4/3',
                  borderRadius: '.8rem',
                  overflow: 'hidden',
                  border: isSelected
                    ? `.4rem solid ${BLUE_600}`
                    : '.3rem solid transparent',
                  transition: 'all 0.2s ease-in-out',
                  position: 'relative',
                  backgroundColor: GRAY_100,
                }}
              >
                {!failedImages.has(template.id) && (
                  <img
                    src={template.thumbnailUrl}
                    alt={t(template.nameKey)}
                    onLoadStart={() => handleImageLoadStart(template.id)}
                    onLoad={() => handleImageLoad(template.id)}
                    onError={() => handleImageError(template.id)}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      display: loadingImages.has(template.id) ? 'none' : 'block',
                    }}
                  />
                )}

                {/* Loading placeholder */}
                {loadingImages.has(template.id) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: GRAY_200,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" fontSize="1rem">
                      {t('virtual_staging.styles.loading')}
                    </Typography>
                  </Box>
                )}

                {/* Error placeholder */}
                {failedImages.has(template.id) && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: GRAY_100,
                      border: `0.1rem dashed ${GRAY_400}`,
                    }}
                  >
                    <Typography variant="caption" color="text.secondary" sx={{ textAlign: 'center', fontSize: '10px' }}>
                      {t('virtual_staging.styles.image_error')}
                    </Typography>
                  </Box>
                )}
              </Box>

              {/* Template Name */}
              <Typography
                variant="caption"
                sx={{
                  display: 'block',
                  textAlign: 'center',
                  marginTop: '0.8rem',
                  fontSize: '1.2rem',
                  fontWeight: isSelected ? 600 : 400,
                  lineHeight: 1.2,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  color: isSelected ? GRAY_900 : GRAY_700
                }}
              >
                {t(template.nameKey)}
              </Typography>
            </Box>
          )
        })}
      </Stack>
    </Stack>
  )
}
